import { useEffect, useState } from 'react';
import { getProviders, createNewProvider } from 'services/orders';
import { IproviderInfo } from 'types/orders';
import useDebounce from 'hooks/useDebounce';
import './ProviderDropdown.scss';

interface IProviderDropdown {
  label?: string;
  value: string;
  countryId: number;
  countryCode: string;
  isDeactivated?: boolean;
  onChange: (name: string, id: number) => void;
}

/**
 * Selector de proveedor con búsqueda en tiempo real y creación inline.
 * Filtra resultados por país y permite crear un nuevo proveedor si no existe.
 * Si el input pierde el foco sin que se haya seleccionado o creado un proveedor,
 * el texto tipeado se revierte al último valor confirmado para no aparentar una selección.
 * Si `isDeactivated` es true, el input queda deshabilitado y no se puede interactuar.
 */
const ProviderDropdown = ({ label = 'Proveedor*', value, countryId, countryCode, isDeactivated, onChange }: IProviderDropdown) => {
  const [searchString, setSearchString] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [providers, setProviders] = useState<IproviderInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebounce(searchString, 500);

  const isFilled = searchString.length > 0;

  /** Sincroniza el input cuando el valor externo cambia (ej. al cambiar de ítem). */
  useEffect(() => {
    setSearchString(value);
  }, [value]);

  /** Busca proveedores cuando cambia el query debounceado. El backend filtra por texto; countryId solo aplica al crear. */
  useEffect(() => {
    if (!debouncedSearch) {
      setProviders([]);
      return;
    }
    setIsLoading(true);
    getProviders(debouncedSearch.toLowerCase())
      .then(results => setProviders(results ?? []))
      .finally(() => setIsLoading(false));
  }, [debouncedSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchString(newValue);
    setIsOpen(newValue.length > 0);
  };

  /** Al perder el foco, cierra el dropdown y revierte el texto al último valor confirmado. */
  const handleBlur = () => {
    setIsOpen(false);
    setSearchString(value);
  };

  const handleSelect = (provider: IproviderInfo) => {
    setSearchString(provider.name);
    setIsOpen(false);
    onChange(provider.name, provider.id);
  };

  /** Crea un nuevo proveedor con el texto ingresado y lo selecciona automáticamente. */
  const handleCreateNew = async () => {
    if (!searchString.trim()) return;
    setIsLoading(true);
    createNewProvider({ name: searchString.trim(), countryId, countryCode })
      .then(created => {
        if (created?.name && created?.id) {
          setSearchString(created.name);
          setIsOpen(false);
          onChange(created.name, created.id);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const showDropdown = isOpen && searchString.length > 0;

  return (
    <div className={`providerDropdown w264${isFilled ? ' filled' : ''}${isDeactivated ? ' deactivated' : ''}`}>
      {label && <label>{label}</label>}
      <div className="inputWrapper">
        <input
          value={searchString}
          placeholder="Selecciona el proveedor"
          readOnly={isDeactivated}
          onChange={isDeactivated ? undefined : handleInputChange}
          onFocus={() => !isDeactivated && searchString.length > 0 && setIsOpen(true)}
          onBlur={handleBlur}
          autoComplete="off"
        />
        {showDropdown && (
          <div className="dropdownList" onMouseDown={e => e.preventDefault()}>
            {isLoading && <div className="dropdownItem loading">Buscando...</div>}
            {!isLoading && providers.map(provider => (
              <div key={provider.id} className="dropdownItem" onMouseDown={() => handleSelect(provider)}>
                {provider.name}
              </div>
            ))}
            {!isLoading && searchString.trim() && (
              <div className="dropdownItem createNew" onMouseDown={handleCreateNew}>
                <span className="createLabel">Crear:</span> {searchString.trim()}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderDropdown;
